import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../ui/select';
import { Globe, Save } from 'lucide-react';

export default function AdminLocalisation({ config, setConfig, onSave, saving }) {
  const { t } = useLanguage();

  return (
    <div className="border border-border rounded-lg p-6 bg-card space-y-6">
      <div>
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Globe className="h-5 w-5" />
          Configuration de la Localisation
        </h2>
        <p className="text-sm text-muted-foreground">Définissez le fuseau horaire et la localisation du centre</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Fuseau horaire</Label>
            <Select 
              value={config.timezone || 'Europe/Zurich'} 
              onValueChange={(v) => setConfig({ ...config, timezone: v })}
            >
              <SelectTrigger data-testid="timezone-select">
                <SelectValue placeholder="Sélectionner..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Europe/Zurich">Europe/Zurich (Suisse)</SelectItem>
                <SelectItem value="Europe/Paris">Europe/Paris (France)</SelectItem>
                <SelectItem value="Europe/Berlin">Europe/Berlin (Allemagne)</SelectItem>
                <SelectItem value="Europe/London">Europe/London (UK)</SelectItem>
                <SelectItem value="UTC">UTC</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Utilisé pour l'affichage des heures dans les exports PDF</p>
          </div>

          <div className="space-y-2">
            <Label>Localisation</Label>
            <Input 
              value={config.location_name || 'Altstätten (SG)'} 
              onChange={(e) => setConfig({ ...config, location_name: e.target.value })}
              placeholder="Altstätten (SG)"
              data-testid="location-name-input"
            />
            <p className="text-xs text-muted-foreground">Nom de la localité affiché dans les documents</p>
          </div>

          <div className="space-y-2">
            <Label>Pays</Label>
            <Select 
              value={config.country || 'CH'} 
              onValueChange={(v) => setConfig({ ...config, country: v })}
            >
              <SelectTrigger data-testid="country-select">
                <SelectValue placeholder="Sélectionner..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CH">Suisse</SelectItem>
                <SelectItem value="FR">France</SelectItem>
                <SelectItem value="DE">Allemagne</SelectItem>
                <SelectItem value="AT">Autriche</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Utilisé pour le calcul des jours fériés</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <h3 className="font-medium text-blue-800 dark:text-blue-300 mb-2">Heure actuelle</h3>
            <p className="text-2xl font-mono text-blue-900 dark:text-blue-200" id="current-time">
              {new Date().toLocaleString('de-CH', { timeZone: config.timezone || 'Europe/Zurich' })}
            </p>
            <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
              Fuseau: {config.timezone || 'Europe/Zurich'}
            </p>
          </div>

          <div className="p-4 bg-muted rounded-lg">
            <h3 className="font-medium mb-2">Configuration actuelle</h3>
            <ul className="text-sm space-y-1 text-muted-foreground">
              <li>• Localisation: <strong>{config.location_name || 'Altstätten (SG)'}</strong></li>
              <li>• Pays: <strong>{config.country === 'CH' ? 'Suisse' : config.country || 'Suisse'}</strong></li>
              <li>• Fuseau horaire: <strong>{config.timezone || 'Europe/Zurich'}</strong></li>
            </ul>
          </div>
        </div>
      </div>

      <div className="flex gap-3 pt-4 border-t border-border">
        <Button
          onClick={() => onSave('localisation')}
          disabled={saving}
          data-testid="save-localisation-config-btn"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? t('saving') : t('save')}
        </Button>
      </div>
    </div>
  );
}
